import { useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Upload, Download, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import {
  parseCSV,
  validateAthletes,
  downloadTemplate,
  downloadErrorReport,
  type ParsedResult,
  type AthleteRow,
} from "@/utils/athleteImport";

interface BulkImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const BulkImportDialog = ({ open, onOpenChange }: BulkImportDialogProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [validationResult, setValidationResult] = useState<ParsedResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && (droppedFile.name.endsWith('.csv') || droppedFile.name.endsWith('.xlsx'))) {
      handleFileSelect(droppedFile);
    } else {
      toast({
        title: "Invalid file type",
        description: "Please upload a CSV or Excel file",
        variant: "destructive",
      });
    }
  }, []);

  const handleFileSelect = async (selectedFile: File) => {
    setFile(selectedFile);
    setValidationResult(null);
    setProgress(0);
    
    try {
      setIsProcessing(true);
      
      // Parse CSV
      const data = await parseCSV(selectedFile);
      
      // Get existing emails from database
      const { data: existingAthletes } = await supabase
        .from("athletes")
        .select("email");
      
      const existingEmails = new Set(
        existingAthletes?.map(a => a.email.toLowerCase()) || []
      );
      
      // Validate
      const result = validateAthletes(data, existingEmails);
      setValidationResult(result);
      
      if (result.invalid.length === 0) {
        toast({
          title: "Validation successful",
          description: `All ${result.summary.valid} athletes are ready to import`,
        });
      } else {
        toast({
          title: "Validation completed with errors",
          description: `${result.summary.valid} valid, ${result.summary.invalid} invalid`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error processing file:", error);
      toast({
        title: "Error processing file",
        description: "Please check your CSV format and try again",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleImport = async () => {
    if (!validationResult || validationResult.valid.length === 0) return;
    
    try {
      setIsProcessing(true);
      setProgress(0);
      
      const athletes = validationResult.valid.map(v => ({
        name: v.data.name,
        email: v.data.email,
        phone: v.data.phone || null,
        sport: v.data.sport,
        year: v.data.year,
        gender: v.data.gender,
        eligibility_status: 'eligible' as const,
        active_deals_count: 0,
        total_deals_value: 0,
      }));
      
      // Batch insert in chunks of 100
      const chunkSize = 100;
      let imported = 0;
      
      for (let i = 0; i < athletes.length; i += chunkSize) {
        const chunk = athletes.slice(i, i + chunkSize);
        const { error } = await supabase
          .from("athletes")
          .insert(chunk);
        
        if (error) throw error;
        
        imported += chunk.length;
        setProgress((imported / athletes.length) * 100);
      }
      
      // Create audit log
      await supabase.from("audit_logs").insert({
        action: "bulk_import_athletes",
        entity_type: "athletes",
        severity: "info",
        details: `Bulk imported ${imported} athletes`,
        metadata: {
          file_name: file?.name,
          total_rows: validationResult.summary.total,
          successful: imported,
          failed: validationResult.summary.invalid,
        },
      });
      
      toast({
        title: "Import successful",
        description: `Successfully imported ${imported} athletes`,
      });
      
      queryClient.invalidateQueries({ queryKey: ["athletes"] });
      onOpenChange(false);
      
      // Reset state
      setFile(null);
      setValidationResult(null);
      setProgress(0);
    } catch (error) {
      console.error("Error importing athletes:", error);
      toast({
        title: "Import failed",
        description: "An error occurred during import. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Import Athletes</DialogTitle>
          <DialogDescription>
            Upload a CSV file to import multiple athletes at once
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Template Download */}
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div>
              <p className="font-medium">Need a template?</p>
              <p className="text-sm text-muted-foreground">
                Download our CSV template to get started
              </p>
            </div>
            <Button variant="outline" onClick={downloadTemplate}>
              <Download className="mr-2 h-4 w-4" />
              Download Template
            </Button>
          </div>

          {/* File Upload */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragging
                ? "border-primary bg-primary/5"
                : "border-muted-foreground/25"
            }`}
          >
            <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium mb-2">
              {file ? file.name : "Drop your CSV file here"}
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              or click to browse
            </p>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => {
                const selectedFile = e.target.files?.[0];
                if (selectedFile) handleFileSelect(selectedFile);
              }}
              className="hidden"
              id="csv-upload"
            />
            <label htmlFor="csv-upload">
              <Button variant="secondary" asChild>
                <span>Choose File</span>
              </Button>
            </label>
          </div>

          {/* Validation Results */}
          {validationResult && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <Alert>
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription>
                    <div className="font-medium">Valid</div>
                    <div className="text-2xl">{validationResult.summary.valid}</div>
                  </AlertDescription>
                </Alert>
                
                <Alert variant="destructive">
                  <XCircle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="font-medium">Invalid</div>
                    <div className="text-2xl">{validationResult.summary.invalid}</div>
                  </AlertDescription>
                </Alert>
                
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="font-medium">Total</div>
                    <div className="text-2xl">{validationResult.summary.total}</div>
                  </AlertDescription>
                </Alert>
              </div>

              {validationResult.invalid.length > 0 && (
                <Alert variant="destructive">
                  <AlertDescription className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Validation errors found</p>
                      <p className="text-sm">Download the error report to see details</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadErrorReport(validationResult.invalid)}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Error Report
                    </Button>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* Progress */}
          {isProcessing && progress > 0 && (
            <div className="space-y-2">
              <Progress value={progress} />
              <p className="text-sm text-center text-muted-foreground">
                Importing athletes... {Math.round(progress)}%
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              disabled={
                !validationResult ||
                validationResult.valid.length === 0 ||
                isProcessing
              }
            >
              Import {validationResult?.summary.valid || 0} Athletes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
